import { useTranslation } from 'react-i18next';
import { Field, Flex } from '@chakra-ui/react';
import { InputField, SelectInput } from '@themes/aegix';
import { useFormContext } from 'react-hook-form';
import { getRooms, ResourceCreate } from '../../services';
import { useQuery } from '@tanstack/react-query';
interface Option {
  label: string;
  value: string;
}
interface ResourceFormProps {
  siteOptions: Option[];
  poiTypes: Option[];
  action: 'add' | 'edit';
}
const ResourceForm: React.FC<ResourceFormProps> = ({
  siteOptions,
  poiTypes,
  action,
}) => {
  const { t } = useTranslation();
  const {
    register,
    setValue,
    watch,
    formState: { errors, touchedFields },
  } = useFormContext<ResourceCreate>();
  const school = watch('school');
  const { data: roomsData = [] } = useQuery({
    queryKey: ['getRooms', school],
    queryFn: async () => {
      const response = await getRooms({
        school: String(school),
        limit: 1000,
      });
      const selectValue =
        response?.results?.map((item) => ({
          label: item.name,
          value: String(item.id),
        })) ?? [];
      return selectValue;
    },
    enabled: Boolean(school),
  });
  return (
    <Flex flexDirection={'column'} gap={4}>
      <Field.Root invalid={!!touchedFields.school && !!errors.school}>
        <SelectInput
          value={school == null ? '' : String(school)}
          name="school"
          label={t('sites.col.site', 'Site')}
          option={siteOptions}
          onChange={(val: string) =>
            setValue('school', Number(val), { shouldTouch: true })
          }
          disabled={action === 'edit'}
          error={touchedFields.school ? errors.school?.message : undefined}
          e2eTitle="resource-site-select"
        />
      </Field.Root>
      <Field.Root invalid={!!touchedFields.name && !!errors.name}>
        <Field.Label>{t('common.name', 'Name')}</Field.Label>
        <InputField
          placeholder={t('resources.resourceName', 'Resource Name')}
          {...register('name')}
          data-e2e="resource-name-input"
        />
        {touchedFields.name && errors.name && (
          <Field.ErrorText data-e2e="resource-name-error">{errors.name.message ? t(errors.name.message) : null}</Field.ErrorText>
        )}
      </Field.Root>

      <Field.Root invalid={!!touchedFields.type && !!errors.type}>
        <SelectInput
          value={watch('type')}
          name="type"
          label={t('resources.col.type', 'Type')}
          option={poiTypes}
          onChange={(val: string) =>
            setValue('type', val, { shouldTouch: true })
          }
          error={touchedFields.type ? errors.type?.message : undefined}
          e2eTitle="resource-type-select"
        />
      </Field.Root>
      <Field.Root invalid={!!touchedFields.room && !!errors.room}>
        <SelectInput
          value={watch('room') == null ? '' : String(watch('room'))}
          name="room"
          label={t('resources.col.location', 'Location')}
          option={roomsData}
          onChange={(val: string) =>
            setValue('room', Number(val), { shouldTouch: true })
          }
          error={touchedFields.room ? errors.room?.message : undefined}
          e2eTitle="resource-location-select"
        />
      </Field.Root>
    </Flex>
  );
};
export default ResourceForm;
